import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await fetch('/api/contact/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      toast({
        title: "Message sent!",
        description: "We'll get back to you as soon as possible.",
        duration: 3000,
      });
      setFormData({
        name: '',
        phone: '',
        email: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-12" style={{ backgroundColor: '#f7f5f2' }}>
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold mb-4" style={{ color: '#4a2c1a', fontFamily: 'Georgia, serif' }}>
            Get in Touch
          </h1>
          <p className="text-xl" style={{ color: '#736c64' }}>
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: '#4a2c1a' }}>
              Send Us a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-base font-semibold" style={{ color: '#4a2c1a' }}>
                  Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-2 border-2"
                  style={{ borderColor: '#e5d5c0' }}
                  placeholder="Your full name"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-base font-semibold" style={{ color: '#4a2c1a' }}>
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-2 border-2"
                  style={{ borderColor: '#e5d5c0' }}
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-base font-semibold" style={{ color: '#4a2c1a' }}>
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-2 border-2"
                  style={{ borderColor: '#e5d5c0' }}
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-base font-semibold" style={{ color: '#4a2c1a' }}>
                  Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  className="mt-2 border-2 min-h-[150px]"
                  style={{ borderColor: '#e5d5c0' }}
                  placeholder="Tell us how we can help you..."
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full text-white font-semibold py-6 text-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#FF8C19' }}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#4a2c1a' }}>
                Contact Information
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FF8C19' }}>
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1" style={{ color: '#4a2c1a' }}>
                      Location
                    </h3>
                    <p style={{ color: '#736c64' }}>
                      Saratoga Springs, NY 12866
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FF8C19' }}>
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1" style={{ color: '#4a2c1a' }}>
                      Email
                    </h3>
                    <p style={{ color: '#736c64' }}>
                      themapledaddies@gmail.com
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8" style={{ backgroundColor: '#4a2c1a' }}>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#FF8C19' }}>
                Business Hours
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span style={{ color: '#f7f5f2' }}>Season:</span>
                  <span className="font-semibold" style={{ color: '#f7f5f2' }}>Late Feb - April</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: '#f7f5f2' }}>Response Time:</span>
                  <span className="font-semibold" style={{ color: '#f7f5f2' }}>24-48 hours</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
